import * as actions from 'redux/constants';
import {
  iframeMessages,
  getDefaultMessages,
  sendErrorMessage
} from 'services/iframeService';
import {
  HOSTED_PAGE_TYPES,
  DigitalWalletProvider,
  PAYMENT_ERROR_QUERY_PARAM_NAME
} from 'consts';
import {
  checkIsCompletedPayment,
  isAwaitingNextAction,
  is3DSPending,
  parseQueryParams
} from 'utilities';

const {
  sendGetPageFailMsg: sendGetCheckoutPageFail,
  sendStartMsg: sendStartMessageCheckout,
  sendSuccessMsg: sendSuccessMessageCheckout,
  sendFailMsg: sendFailMessageCheckout
} = getDefaultMessages(HOSTED_PAGE_TYPES.checkout);

const {
  sendGetPageFailMsg: sendGetCardTokenizationPageFail,
  sendStartMsg: sendStartMessageCardTokenization,
  sendSuccessMsg: sendSuccessMessageCardTokenization,
  sendFailMsg: sendFailMessageCardTokenization
} = getDefaultMessages(HOSTED_PAGE_TYPES.cardTokenization);

const collectMiddleware = store => next => action => {
  const {
    global: { isSdk, sdkParams }
  } = store.getState();
  const { waitOnPaymentRedirect, waitOnPaymentConfirmation } = sdkParams || {};
  if (!isSdk) {
    return next(action);
  }

  switch (action.type) {
    case actions.GET_CHECKOUT_PAGE_FAIL: {
      sendGetCheckoutPageFail(action.payload.error);
      break;
    }
    case actions.GET_CHECKOUT_PAGE_SUCCESS: {
      const isAlreadyInUse = action.payload?.checkoutAlreadyInUse ?? false;
      if (!action.payload || !isAlreadyInUse) {
        break;
      }
      const { authorizeFlowRedirect } = action.payload;
      if (!authorizeFlowRedirect && !waitOnPaymentRedirect) {
        sendGetCheckoutPageFail('CHECKOUT_ALREADY_IN_USE');
        break;
      }
      const checkoutPageData = action.payload.data || {};
      const parentMessage = checkIsCompletedPayment(checkoutPageData)
        ? sendSuccessMessageCheckout.bind(this, {
            eventType: iframeMessages.CHECKOUT_CREATE_PAYMENT_SUCCESS,
            data: {
              ...checkoutPageData.payment
            },
            meta: {
              closeIframe: true,
              withoutLoading: true
            }
          })
        : sendFailMessageCheckout.bind(this, {
            eventType: iframeMessages.CHECKOUT_CREATE_PAYMENT_FAIL,
            error: 'ERROR_CREATE_HOSTED_PAGE_PAYMENT_AUTHORIZATION_FLOW',
            meta: {
              closeIframe: true,
              withoutLoading: true
            }
          });
      parentMessage();
      break;
    }
    case actions.CREATE_PAYMENT_START:
      sendStartMessageCheckout();
      break;
    case actions.CREATE_PAYMENT_SUCCESS: {
      const { next_action: nextAction, status } = action.payload.payment || {};
      const isPaymentWaitOnPaymentConfirmation =
        waitOnPaymentConfirmation &&
        isAwaitingNextAction({
          nextAction,
          status
        });

      const isPaymentWaitOnPaymentRedirect =
        waitOnPaymentRedirect && is3DSPending({ nextAction, status });
      const pendingPayment =
        isPaymentWaitOnPaymentRedirect || isPaymentWaitOnPaymentConfirmation;

      const isGooglePayPayment =
        action.payload.digitalWalletProvider === DigitalWalletProvider.GOOGLE_PAY;

      sendSuccessMessageCheckout({
        eventType: pendingPayment
          ? iframeMessages.CHECKOUT_PAYMENT_PENDING
          : iframeMessages.CHECKOUT_CREATE_PAYMENT_SUCCESS,
        data: {
          ...action.payload.payment,
          ...(isGooglePayPayment && {
            digital_wallet: DigitalWalletProvider.GOOGLE_PAY
          })
        },
        meta: {
          closeIframe: !pendingPayment && !isGooglePayPayment
        }
      });
      break;
    }
    case actions.CREATE_PAYMENT_FAIL: {
      const isGooglePayPayment =
        action.meta.digitalWalletProvider === DigitalWalletProvider.GOOGLE_PAY;
      const closeIframe = !isGooglePayPayment;
      sendFailMessageCheckout({
        eventType: iframeMessages.CHECKOUT_CREATE_PAYMENT_FAIL,
        error: action.payload.error,
        meta: {
          closeIframe
        }
      });
      break;
    }
    case actions.EDIT_CARD_START: {
      sendStartMessageCheckout();
      break;
    }
    case actions.EDIT_CARD_SUCCESS: {
      const { oldCardId, newCard } = action.meta;
      sendSuccessMessageCheckout({
        eventType: iframeMessages.CHECKOUT_EDIT_CARD_SUCCESS,
        data: {
          new_card: newCard,
          old_card_id: oldCardId
        }
      });
      break;
    }
    case actions.EDIT_CARD_FAIL: {
      sendFailMessageCheckout({
        eventType: iframeMessages.CHECKOUT_EDIT_CARD_FAIL,
        error: action.payload.error
      });
      break;
    }
    case actions.DELETE_CARD_START: {
      sendStartMessageCheckout();
      break;
    }
    case actions.DELETE_CARD_SUCCESS: {
      const { id } = action.meta;
      sendSuccessMessageCheckout({
        eventType: iframeMessages.CHECKOUT_DELETE_CARD_SUCCESS,
        data: { id }
      });
      break;
    }
    case actions.DELETE_CARD_FAIL: {
      sendFailMessageCheckout({
        eventType: iframeMessages.CHECKOUT_DELETE_CARD_FAIL,
        error: action.payload.error
      });
      break;
    }
    case actions.GET_CARD_TOKENIZATION_PAGE_FAIL: {
      sendGetCardTokenizationPageFail(action.payload.error);
      break;
    }
    case actions.GET_CARD_TOKENIZATION_PAGE_SUCCESS: {
      const queryParams = parseQueryParams() || {};
      const paymentErrorFromQS = queryParams?.[PAYMENT_ERROR_QUERY_PARAM_NAME];
      const cardTokenizationPageData = action.payload.data || {};
      const { status, customer, customer_card_payment_methods } =
        cardTokenizationPageData || {};

      if (waitOnPaymentRedirect) {
        if (paymentErrorFromQS) {
          sendFailMessageCardTokenization({
            eventType: iframeMessages.CARD_TOKENIZATION_CREATE_FAIL,
            error: 'ERROR_TOKENIZE_CARD_FAIL_ON_3DS',
            meta: {
              closeIframe: true
            }
          });
          break;
        }

        if (status === 'DON') {
          sendSuccessMessageCardTokenization({
            eventType: iframeMessages.CARD_TOKENIZATION_CREATE_SUCCESS,
            data: { customer, payment_method: customer_card_payment_methods?.[0]?.id },
            meta: {
              closeIframe: true,
              withoutLoading: true
            }
          });
          break;
        }
      }

      if (
        action.payload &&
        (action.payload.data?.status || action.payload.status) !== 'NEW'
      ) {
        sendGetCardTokenizationPageFail('CARD_PAGE_ALREADY_IN_USE');
        break;
      }

      break;
    }
    case actions.TOKENIZE_CARD_START: {
      sendStartMessageCardTokenization();
      break;
    }
    case actions.TOKENIZE_CARD_SUCCESS: {
      const { next_action: nextAction, status } = action?.payload?.data || {};
      const isPaymentWaitOnPaymentRedirect =
        waitOnPaymentRedirect && is3DSPending({ nextAction, status });

      sendSuccessMessageCardTokenization({
        eventType: isPaymentWaitOnPaymentRedirect
          ? iframeMessages.CARD_TOKENIZATION_CREATE_PENDING
          : iframeMessages.CARD_TOKENIZATION_CREATE_SUCCESS,
        data: action.payload.data,
        meta: {
          closeIframe: !isPaymentWaitOnPaymentRedirect
        }
      });
      break;
    }

    case actions.TOKENIZE_CARD_FAIL: {
      sendFailMessageCardTokenization({
        eventType: iframeMessages.CARD_TOKENIZATION_CREATE_FAIL,
        error: action.payload.error,
        meta: {
          closeIframe: true
        }
      });
      break;
    }

    case actions.SET_DEFAULT_CARD_SUCCESS: {
      const { defaultPmId } = action.payload;
      const defaultCard = store
        .getState()
        .customer.customerPaymentMethods?.data?.card?.find(
          card => card.id === defaultPmId
        );
      sendSuccessMessageCardTokenization({
        eventType: iframeMessages.CARD_TOKENIZATION_ON_UPDATE,
        data: {
          ...defaultCard,
          default: true
        }
      });
      break;
    }
    case actions.NOTIFY_CARD_TOKENIZATION_FAIL: {
      sendErrorMessage({
        type: iframeMessages.CARD_TOKENIZATION_ON_FAILURE,
        error: 'ERROR_HOSTED_PAGE_FAILED_TO_CREATE_CARD_TOKEN',
        meta: { closeIframe: true }
      });
      break;
    }
  }

  next(action);
};

export default collectMiddleware;
